import moment from 'moment';
import { upload } from 'src/helpers/upLoadHelper';

interface BuildSuiteParamsBase {
  suiteData: any;
  userId: any;
  imageUrl: any;
  isNewImage: boolean;
  parkingStallsArray: any[];
  storageLockerArray: any[];
  areas: any[];
}

interface BuildSuiteCreateParams extends BuildSuiteParamsBase {
  propertyId: any;
}

const toDateOnly = (value: any): string | null => {
  if (!value) {
    return null;
  }

  return moment(value).format('YYYY-MM-DD');
};

const toWarrantyStartDate = (value: any): Date | null => {
  if (!value) {
    return null;
  }

  return new Date(moment(value).format('l LT'));
};

export const resolveFloorPlanUrl = async (
  floorPlanURL: any,
  floorPlanImage: any
): Promise<any> => {
  if (floorPlanImage === floorPlanURL) {
    return floorPlanURL;
  }

  if (floorPlanURL?.length > 0) {
    return await upload(floorPlanURL[0]);
  }

  return null;
};

export const mapStallEntries = (
  entries: any[] | undefined,
  type: 'ParkingStalls' | 'StorageLockers'
): any[] => {
  if (!entries) {
    return [];
  }

  return entries.map((entry: any) => ({
    Name: entry.label,
    Type: type,
  }));
};

// Pure: the API treats an `Other` area's client-generated id as invalid, so drop
// it. Returns a new array — does not mutate the caller's areas.
export const stripOtherAreaIds = (areas: any[] | undefined): any[] => {
  if (!areas) {
    return [];
  }

  return areas.map((area: any) => {
    if (area.type === 'Other') {
      const { id, ...rest } = area;
      return rest;
    }

    return area;
  });
};

export const buildSuiteUpdateParameters = ({
  suiteData,
  userId,
  imageUrl,
  isNewImage,
  parkingStallsArray,
  storageLockerArray,
  areas,
}: BuildSuiteParamsBase) => ({
  id: suiteData.id,
  statusId: suiteData.statusId,
  propertyId: suiteData.propertyId,
  number: suiteData.number.trim(),
  stratalot: suiteData.stratalot ? suiteData.stratalot.trim() : '',
  IsCommonArea: !!suiteData.isCommonArea,
  Userid: userId,
  Areas: areas,
  floorPlanURL: imageUrl,
  assetWarrantyDefaultStartDate: toWarrantyStartDate(
    suiteData.assetWarrantyDefaultStartDate
  ),
  warrantyNumber: suiteData.warrantyNumber,
  warrantyAddress: suiteData.warrantyAddress,
  Building: suiteData.Building,
  BuildingId: suiteData.BuildingId || null,
  Level: suiteData.Level,
  LevelId: suiteData.LevelId || null,
  ParkingStalls: parkingStallsArray,
  StorageLockers: storageLockerArray,
  newImage: isNewImage,
  KeyFobs: suiteData.keyFobs,
  NumberOfKeys:
    suiteData.numberOfKeys === null || suiteData.numberOfKeys === undefined
      ? 0
      : Number(suiteData.numberOfKeys),
  KeyHandOffInstructions: suiteData.keyHandOffInstructions,
  suiteKeysAndValues: suiteData.suiteKeysAndValues,
  OrientationDate: toDateOnly(suiteData.orientationDate),
  CompletionDate: toDateOnly(suiteData.completionDate),
  PosessionDate: toDateOnly(suiteData.posessionDate),
});

export const buildSuiteCreateParameters = ({
  suiteData,
  userId,
  propertyId,
  imageUrl,
  isNewImage,
  parkingStallsArray,
  storageLockerArray,
  areas,
}: BuildSuiteCreateParams) => ({
  fromUnitId: suiteData.fromUnitId ?? null,
  Number: suiteData.number.trim(),
  stratalot: suiteData.stratalot ? suiteData.stratalot.trim() : '',
  PropertyId: propertyId,
  StatusId: suiteData.statusId,
  IsCommonArea: !!suiteData.isCommonArea,
  Userid: userId,
  Areas: areas,
  floorPlanURL: imageUrl,
  assetWarrantyDefaultStartDate: toWarrantyStartDate(
    suiteData.assetWarrantyDefaultStartDate
  ),
  warrantyNumber: suiteData.warrantyNumber,
  warrantyAddress: suiteData.warrantyAddress,
  Building: suiteData.Building,
  BuildingId: suiteData.BuildingId || null,
  Level: suiteData.Level,
  LevelId: suiteData.LevelId || null,
  ParkingStalls: parkingStallsArray,
  StorageLockers: storageLockerArray,
  newImage: isNewImage,
  KeyFobs: suiteData.keyFobs,
  NumberOfKeys:
    suiteData.numberOfKeys === null ||
    suiteData.numberOfKeys === '' ||
    suiteData.numberOfKeys === undefined
      ? 0
      : Number(suiteData.numberOfKeys),
  KeyHandOffInstructions: suiteData.keyHandOffInstructions,
  suiteKeysAndValues: suiteData.suiteKeysAndValues,
  OrientationDate: toDateOnly(suiteData.orientationDate),
  CompletionDate: toDateOnly(suiteData.completionDate),
  PosessionDate: toDateOnly(suiteData.posessionDate),
});
